// ── WhatsAppTemplatesPage ──────────────────────────────────────────
//
// Lists, creates, and manages WhatsApp message templates.
// Supports filtering by status, category, and search.
// Template creation uses a simple component builder (HEADER + BODY + FOOTER + BUTTONS).

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Loader2,
  PlusCircle,
  RefreshCw,
  Search,
  Trash2,
  XCircle,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import {
  listWhatsappAccounts,
  listTemplates,
  deleteTemplate,
  syncTemplates,
  createTemplate,
  type WhatsappAccountListItem,
  type WhatsappTemplate,
  type TemplateStatus,
  type TemplateCategory,
} from '../api/whatsapp.api';
import { formatDate } from '@/lib/dateFormat';
import { SelectField } from '@/components/ui/SelectField';

// ── Status / category display helpers ────────────────────────────

const STATUS_BADGE: Record<TemplateStatus, string> = {
  APPROVED: 'bg-emerald-50 text-emerald-700',
  PENDING: 'bg-amber-50 text-amber-700',
  REJECTED: 'bg-red-50 text-red-700',
  PAUSED: 'bg-orange-50 text-orange-700',
  DISABLED: 'bg-slate-100 text-slate-500',
  DELETED: 'bg-slate-100 text-slate-400',
};

const STATUS_ICONS: Record<TemplateStatus, React.ReactNode> = {
  APPROVED: <CheckCircle2 className="h-3.5 w-3.5" />,
  PENDING: <Clock className="h-3.5 w-3.5" />,
  REJECTED: <XCircle className="h-3.5 w-3.5" />,
  PAUSED: <AlertCircle className="h-3.5 w-3.5" />,
  DISABLED: <XCircle className="h-3.5 w-3.5" />,
  DELETED: <XCircle className="h-3.5 w-3.5" />,
};

const CATEGORY_BADGE: Record<TemplateCategory, string> = {
  MARKETING: 'bg-purple-50 text-purple-700',
  UTILITY: 'bg-blue-50 text-blue-700',
  AUTHENTICATION: 'bg-teal-50 text-teal-700',
};

// Pull the BODY text from Meta components for preview
function getBodyText(components: any[]): string {
  const body = components?.find((c) => c.type === 'BODY');
  return body?.text ?? '';
}

// Extract the unique {{N}} variable numbers from a string, ascending.
// "Hi {{1}}, your seat for {{2}} is ready" → [1, 2]
function extractVarNumbers(text: string): number[] {
  const nums = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) nums.add(Number(m[1]));
  return [...nums].sort((a, b) => a - b);
}

// ── Create template drawer ────────────────────────────────────────

const CATEGORIES: TemplateCategory[] = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'hi', label: 'Hindi' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt_BR', label: 'Portuguese (BR)' },
  { code: 'ar', label: 'Arabic' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
];

function CreateTemplateDrawer({
  accounts,
  onClose,
  onCreated,
}: {
  accounts: WhatsappAccountListItem[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    whatsappAccountId: accounts[0]?.id ?? '',
    name: '',
    language: 'en',
    category: 'UTILITY' as TemplateCategory,
    headerText: '',
    bodyText: '',
    footerText: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Example value per variable: key 'h1' for the header var, 'b{n}' for body vars.
  // Meta REQUIRES an example for every {{N}} placeholder, or it 400s on submit.
  const [varExamples, setVarExamples] = useState<Record<string, string>>({});

  // ── Buttons (optional) ────────────────────────────────────────────────
  // Meta allows up to 2 Call-to-Action buttons (URL + Phone) OR up to 10
  // Quick Reply buttons per template. Most tenants only need one URL
  // button (e.g. "Join Meeting" on a booking template), so the UI keeps
  // it simple: an "Add button" affordance, each button gets type/text/
  // (url or phone).
  //
  // URL buttons can carry ONE dynamic variable at the end of the URL —
  // Meta requires an `example` array with the sample suffix so reviewers
  // can preview the resolved link. If the URL contains `{{1}}` we treat
  // it as dynamic and prompt for the sample.
  type WaButton =
    | { id: string; type: 'URL'; text: string; url: string; example: string }
    | { id: string; type: 'PHONE_NUMBER'; text: string; phoneNumber: string }
    | { id: string; type: 'QUICK_REPLY'; text: string };
  const [buttons, setButtons] = useState<WaButton[]>([]);

  const newBtnId = () => `btn_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const addButton = (type: WaButton['type']) => {
    if (buttons.length >= 3) return;
    setButtons((prev) => {
      if (type === 'URL') return [...prev, { id: newBtnId(), type: 'URL', text: '', url: '', example: '' }];
      if (type === 'PHONE_NUMBER') return [...prev, { id: newBtnId(), type: 'PHONE_NUMBER', text: '', phoneNumber: '' }];
      return [...prev, { id: newBtnId(), type: 'QUICK_REPLY', text: '' }];
    });
  };
  const updateButton = (id: string, patch: Partial<WaButton>) =>
    setButtons((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } as WaButton : b)));
  const removeButton = (id: string) =>
    setButtons((prev) => prev.filter((b) => b.id !== id));

  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));
  const setExample = (key: string, v: string) =>
    setVarExamples((p) => ({ ...p, [key]: v }));

  // Variables detected in the current header / body text.
  const bodyVars = extractVarNumbers(form.bodyText);
  const headerVars = extractVarNumbers(form.headerText); // header supports at most one
  const hasVars = bodyVars.length > 0 || headerVars.length > 0;

  const submit = async () => {
    if (!form.name || !form.bodyText) {
      setError('Name and Body are required.');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(form.name)) {
      setError('Template name must be lowercase snake_case (a-z, 0-9, _).');
      return;
    }
    // Meta requires an example value for every {{N}} variable. Block submit
    // (with a clear message) instead of letting the API 400.
    if (headerVars.length === 1 && !(varExamples['h1'] ?? '').trim()) {
      setError('Please fill the example value for the header variable {{1}}.');
      return;
    }
    if (bodyVars.some((n) => !(varExamples[`b${n}`] ?? '').trim())) {
      setError('Please fill an example value for every body variable ({{1}}, {{2}}, …).');
      return;
    }
    // Validate each configured button. Meta rejects (#100) any URL that
    // isn't a real URI — the value MUST start with http(s):// and if it
    // has a variable, only ONE, and only at the tail. Catch these
    // client-side or the Submit burns a Meta round-trip on an obvious
    // format issue.
    for (const btn of buttons) {
      if (!btn.text.trim()) {
        setError('Every button needs a label.');
        return;
      }
      if (btn.type === 'URL') {
        const url = btn.url.trim();
        if (!url) { setError(`URL is required for the "${btn.text}" button.`); return; }
        if (!/^https?:\/\//i.test(url)) {
          setError(`"${btn.text}" URL must start with https:// — "{{1}}" alone is not a valid URI to Meta. Use e.g. https://us02web.zoom.us/j/{{1}}`);
          return;
        }
        const varCount = (url.match(/\{\{\d+\}\}/g) ?? []).length;
        if (varCount > 1) {
          setError(`"${btn.text}" URL can contain at most ONE variable placeholder (Meta limit).`);
          return;
        }
        if (varCount === 1 && !/\{\{\d+\}\}$/.test(url)) {
          setError(`"${btn.text}" URL variable must be at the end of the URL (Meta limit). e.g. https://example.com/{{1}}`);
          return;
        }
        if (varCount === 1 && !btn.example.trim()) {
          setError(`Provide a sample URL for the "${btn.text}" button since its URL contains a variable.`);
          return;
        }
      }
      if (btn.type === 'PHONE_NUMBER' && !btn.phoneNumber.trim()) {
        setError(`Phone number is required for the "${btn.text}" button.`);
        return;
      }
    }

    setSaving(true);
    setError(null);

    const components: any[] = [];
    if (form.headerText.trim()) {
      const header: any = { type: 'HEADER', format: 'TEXT', text: form.headerText };
      if (headerVars.length === 1) {
        header.example = { header_text: [(varExamples['h1'] ?? '').trim()] };
      }
      components.push(header);
    }
    const body: any = { type: 'BODY', text: form.bodyText };
    if (bodyVars.length > 0) {
      // Meta expects body_text as a 2D array: one row of example values,
      // ordered by variable number ({{1}}, {{2}}, …).
      body.example = { body_text: [bodyVars.map((n) => (varExamples[`b${n}`] ?? '').trim())] };
    }
    components.push(body);
    if (form.footerText.trim()) {
      components.push({ type: 'FOOTER', text: form.footerText });
    }
    if (buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: buttons.map((b) => {
          if (b.type === 'URL') {
            const out: any = { type: 'URL', text: b.text.trim(), url: b.url.trim() };
            // Meta wants the sample resolved URL when the template uses
            // a dynamic tail — pass it via example[] as documented.
            if (b.url.includes('{{')) out.example = [b.example.trim()];
            return out;
          }
          if (b.type === 'PHONE_NUMBER') {
            return { type: 'PHONE_NUMBER', text: b.text.trim(), phone_number: b.phoneNumber.trim() };
          }
          return { type: 'QUICK_REPLY', text: b.text.trim() };
        }),
      });
    }

    try {
      const created = await createTemplate({
        whatsappAccountId: form.whatsappAccountId,
        name: form.name,
        language: form.language,
        category: form.category,
        components,
      });
      // Meta auto-categorises by content and can override the requested
      // category at submit time (commonly UTILITY → MARKETING). Surface
      // that immediately instead of letting it look like a silent change.
      if (created?.category && created.category !== form.category) {
        toast(
          'warning',
          `Meta categorised this template as ${created.category} (you requested ${form.category}). Meta decides the category from the message content.`,
        );
      } else {
        toast('success', 'Template submitted for Meta approval');
      }
      onCreated();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.error ??
        e?.message ??
        'Failed to create template';
      setError(typeof msg === 'string' ? msg : 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-800">New Template</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 px-5 py-4">
          {/* Account */}
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">WhatsApp Account</label>
            <SelectField
              value={form.whatsappAccountId}
              onChange={(e) => set('whatsappAccountId', e.target.value)}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayPhoneNumber} {a.verifiedName ? `(${a.verifiedName})` : ''}
                </option>
              ))}
            </SelectField>
          </div>

          {/* Name */}
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">
              Template name <span className="text-slate-400">(lowercase_snake_case)</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm font-mono"
              placeholder="e.g. order_confirmation"
            />
          </div>

          {/* Category + Language */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-normal text-slate-600">Category</label>
              <SelectField
                value={form.category}
                onChange={(e) => set('category', e.target.value as TemplateCategory)}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </SelectField>
            </div>
            <div>
              <label className="mb-1 block text-xs font-normal text-slate-600">Language</label>
              <SelectField
                value={form.language}
                onChange={(e) => set('language', e.target.value)}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </SelectField>
            </div>
          </div>

          {/* Header (optional) */}
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">
              Header text <span className="text-slate-400">(optional)</span>
            </label>
            <input
              value={form.headerText}
              onChange={(e) => set('headerText', e.target.value)}
              maxLength={60}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              placeholder="Short header (max 60 chars)"
            />
          </div>

          {/* Body */}
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">
              Body <span className="text-red-400">*</span>
            </label>
            <textarea
              value={form.bodyText}
              onChange={(e) => set('bodyText', e.target.value)}
              maxLength={1024}
              rows={5}
              className="w-full resize-none rounded-md border border-gray-200 px-3 py-2 text-sm"
              placeholder="Use {{1}}, {{2}}... for variable placeholders"
            />
            <p className="mt-0.5 text-[11px] text-slate-400">
              {form.bodyText.length}/1024 — use {'{{1}}'}, {'{{2}}'} for variables
            </p>
          </div>

          {/* Footer (optional) */}
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">
              Footer text <span className="text-slate-400">(optional)</span>
            </label>
            <input
              value={form.footerText}
              onChange={(e) => set('footerText', e.target.value)}
              maxLength={60}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              placeholder="e.g. Reply STOP to unsubscribe"
            />
          </div>

          {/* Buttons (optional) — up to 3 total per template. Meta allows
              at most 2 CTA (URL + Phone) OR up to 10 Quick Reply, and
              they can't be mixed. Keeping the cap at 3 covers the common
              case (Join Meeting URL, plus one QR / Phone) without
              modelling the full Meta matrix — most tenants just want
              one URL button. */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-normal text-slate-600">
                Buttons <span className="text-slate-400">(optional, max 3)</span>
              </label>
              {buttons.length < 3 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => addButton('URL')}
                    className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-600"
                  >
                    + URL
                  </button>
                  <button
                    type="button"
                    onClick={() => addButton('PHONE_NUMBER')}
                    className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-600"
                  >
                    + Phone
                  </button>
                  <button
                    type="button"
                    onClick={() => addButton('QUICK_REPLY')}
                    className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-600"
                  >
                    + Quick reply
                  </button>
                </div>
              )}
            </div>

            {buttons.length === 0 ? (
              <p className="text-[11px] text-slate-400">
                No buttons. For a "Join Meeting" link on a booking template, click <b>+ URL</b>.
              </p>
            ) : (
              <div className="space-y-2">
                {buttons.map((b) => (
                  <div key={b.id} className="rounded-lg border border-slate-200 p-2.5">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {b.type.replace('_', ' ')}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeButton(b.id)}
                        className="text-[11px] text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                    <input
                      value={b.text}
                      onChange={(e) => updateButton(b.id, { text: e.target.value } as any)}
                      maxLength={25}
                      className="mb-1.5 w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs"
                      placeholder="Button label (e.g. Join Meeting)"
                    />
                    {b.type === 'URL' && (
                      <>
                        <input
                          value={b.url}
                          onChange={(e) => updateButton(b.id, { url: e.target.value } as any)}
                          className="mb-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs font-mono"
                          placeholder="https://us02web.zoom.us/j/{{1}}"
                        />
                        <p className="mb-1.5 text-[10px] leading-snug text-slate-400">
                          Must start with <code className="text-slate-500">https://</code>. Variable
                          <code className="mx-0.5 rounded bg-slate-100 px-1 text-slate-500">{'{{1}}'}</code>
                          is optional and must sit at the <b>end</b> of the URL. Meta rejects
                          <code className="mx-0.5 rounded bg-slate-100 px-1 text-slate-500">{'{{1}}'}</code>
                          alone as not a valid URI.
                        </p>
                        {b.url.includes('{{') && (
                          <input
                            value={b.example}
                            onChange={(e) => updateButton(b.id, { example: e.target.value } as any)}
                            className="w-full rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs font-mono placeholder:text-amber-400"
                            placeholder="Sample resolved URL (e.g. https://zoom.us/j/1234567890)"
                          />
                        )}
                      </>
                    )}
                    {b.type === 'PHONE_NUMBER' && (
                      <input
                        value={b.phoneNumber}
                        onChange={(e) => updateButton(b.id, { phoneNumber: e.target.value } as any)}
                        className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs font-mono"
                        placeholder="+919876543210"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Example values for variables — required by Meta */}
          {hasVars && (
            <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
              <p className="mb-1 text-xs font-medium text-amber-800">
                Example values <span className="font-normal text-amber-600">(required for approval)</span>
              </p>
              <p className="mb-2.5 text-[11px] text-amber-600">
                Meta needs a sample for each variable so reviewers see how the message reads.
              </p>
              <div className="space-y-2">
                {headerVars.length === 1 && (
                  <div className="flex items-center gap-2">
                    <span className="w-16 flex-shrink-0 font-mono text-[11px] text-slate-500">
                      Header {'{{1}}'}
                    </span>
                    <input
                      value={varExamples['h1'] ?? ''}
                      onChange={(e) => setExample('h1', e.target.value)}
                      className="flex-1 rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-sm"
                      placeholder="e.g. Admissions"
                    />
                  </div>
                )}
                {bodyVars.map((n) => (
                  <div key={n} className="flex items-center gap-2">
                    <span className="w-16 flex-shrink-0 font-mono text-[11px] text-slate-500">
                      {`{{${n}}}`}
                    </span>
                    <input
                      value={varExamples[`b${n}`] ?? ''}
                      onChange={(e) => setExample(`b${n}`, e.target.value)}
                      className="flex-1 rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-sm"
                      placeholder={`Sample value for {{${n}}}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview */}
          {form.bodyText && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-slate-500">Preview</p>
              <div className="rounded-xl bg-[#e9fbe5] p-3 shadow-inner">
                {form.headerText && (
                  <p className="mb-1 text-[13px] font-semibold text-slate-800">
                    {form.headerText.replace(/\{\{1\}\}/g, varExamples['h1']?.trim() || '{{1}}')}
                  </p>
                )}
                <p className="whitespace-pre-wrap text-[13px] text-slate-800">
                  {form.bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => varExamples[`b${n}`]?.trim() || `{{${n}}}`)}
                </p>
                {form.footerText && (
                  <p className="mt-1 text-[11px] text-slate-500">{form.footerText}</p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-200 px-4 py-2 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit for approval
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Template row ──────────────────────────────────────────────────

function TemplateRow({
  template,
  onDelete,
}: {
  template: WhatsappTemplate;
  onDelete: (t: WhatsappTemplate) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const bodyText = getBodyText(template.components);
  const statusClass = STATUS_BADGE[template.status] ?? 'bg-slate-100 text-slate-500';
  const categoryClass = CATEGORY_BADGE[template.category] ?? 'bg-slate-100 text-slate-500';

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="font-mono text-sm font-semibold text-slate-800 hover:text-indigo-600"
            >
              {template.name}
            </button>
            <span className="text-xs text-slate-400">{template.language}</span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClass}`}
            >
              {STATUS_ICONS[template.status]}
              {template.status}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${categoryClass}`}
            >
              {template.category}
            </span>
          </div>
          {bodyText && !expanded && (
            <p className="mt-1 line-clamp-1 text-xs text-slate-500">{bodyText}</p>
          )}
          {template.rejectionReason && (
            <p className="mt-1 text-xs text-red-600">
              Rejection reason: {template.rejectionReason}
            </p>
          )}
        </div>
        <button
          onClick={() => onDelete(template)}
          disabled={template.status === 'DELETED'}
          className="flex-shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
          title="Delete template"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-gray-50 px-4 py-3">
          <div className="rounded-xl bg-[#e9fbe5] p-3">
            {template.components.map((c, i) => (
              <div key={i} className="mb-1 last:mb-0">
                {c.type === 'HEADER' && (
                  <p className="text-[13px] font-semibold text-slate-800">{c.text}</p>
                )}
                {c.type === 'BODY' && (
                  <p className="whitespace-pre-wrap text-[13px] text-slate-800">{c.text}</p>
                )}
                {c.type === 'FOOTER' && (
                  <p className="text-[11px] text-slate-500">{c.text}</p>
                )}
                {c.type === 'BUTTONS' &&
                  c.buttons?.map((btn: any, j: number) => (
                    <div
                      key={j}
                      className="mt-1 rounded-md border border-green-200 bg-white px-3 py-1.5 text-center text-xs text-green-700"
                    >
                      {btn.text}
                    </div>
                  ))}
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Created {formatDate(template.createdAt)}
            {template.metaTemplateId && ` · Meta ID: ${template.metaTemplateId}`}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'DELETED', label: 'Deleted' },
];

export default function WhatsAppTemplatesPage() {
  const [accounts, setAccounts] = useState<WhatsappAccountListItem[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhatsappTemplate | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      // Only CONNECTED accounts are usable for templates — a disconnected
      // (removed) account shouldn't clutter the picker or hide templates.
      const data = (await listWhatsappAccounts()).filter((a) => a.status === 'CONNECTED');
      setAccounts(data);
      // Auto-select when there's a single account (so Sync works and the
      // account dropdown is hidden). With multiple, stay on "All accounts"
      // (empty) — auto-picking the first one hid every template when that
      // first account happened to have none.
      if (data.length === 1 && !selectedAccount) setSelectedAccount(data[0].id);
    } catch {
      /* ignore */
    }
  }, [selectedAccount]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTemplates({
        whatsappAccountId: selectedAccount || undefined,
        status: (status as TemplateStatus) || undefined,
        search: search || undefined,
        page,
        limit: 20,
      });
      // Hide DELETED templates when no specific status is selected —
      // they clutter the list. Users can view them via "Deleted" filter.
      const visible = status ? res.data : res.data.filter((t: any) => t.status !== 'DELETED');
      setTemplates(visible);
      setTotalPages(res.totalPages);
    } catch {
      toast('error', 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, status, search, page]);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSync = async () => {
    if (!selectedAccount) return;
    setSyncing(true);
    try {
      await syncTemplates(selectedAccount);
      toast('success', 'Template sync queued — refresh in a moment');
      setTimeout(() => loadTemplates(), 2500);
    } catch {
      toast('error', 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Provider of the selected account — drives BSP-aware labels/behaviour.
  const selProvider = String((accounts.find((a) => a.id === selectedAccount) as any)?.provider || 'META');
  const isBspSel = selProvider === 'WATI' || selProvider === 'GUPSHUP';
  const providerName = selProvider === 'WATI' ? 'Wati' : selProvider === 'GUPSHUP' ? 'Gupshup' : 'Meta';
  const syncLabel = `Sync from ${providerName}`;

  const handleNewTemplate = () => {
    if (isBspSel) {
      toast('warning', `Create & approve templates in your ${providerName} dashboard, then click “${syncLabel}” to pull them here.`);
      return;
    }
    setCreateOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate(deleteTarget.id);
      toast('success', 'Template deleted');
      setDeleteTarget(null);
      loadTemplates();
    } catch {
      toast('error', 'Failed to delete template');
    }
  };

  return (
    <DashboardLayout title="WhatsApp Templates">
      <div className="mx-auto max-w-4xl space-y-5 px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Message Templates</h1>
            <p className="text-sm text-slate-500">
              Manage pre-approved WhatsApp templates for outbound and broadcast messaging.
              {isBspSel && ` Templates live in your ${providerName} dashboard — click “${syncLabel}” to pull them in.`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={syncing || !selectedAccount}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {syncLabel}
            </button>
            <button
              onClick={handleNewTemplate}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <PlusCircle className="h-4 w-4" />
              New template
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {accounts.length > 1 && (
            <SelectField
              value={selectedAccount}
              onChange={(e) => { setSelectedAccount(e.target.value); setPage(1); }}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm"
            >
              <option value="">All accounts</option>
              {accounts.map((a) => {
                const prov = String((a as any).provider || 'META');
                const provTag = prov === 'WATI' ? ' · Wati' : prov === 'GUPSHUP' ? ' · Gupshup' : '';
                return (
                  <option key={a.id} value={a.id}>
                    {a.displayPhoneNumber}{provTag}
                  </option>
                );
              })}
            </SelectField>
          )}
          <SelectField
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="rounded-md border border-gray-200 px-3 py-1.5 text-sm"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </SelectField>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name…"
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-3 text-sm"
            />
          </div>
        </div>

        {/* Template list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
            <FileText className="mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">No templates found</p>
            <p className="mt-1 text-xs text-slate-400">
              Create a new template or sync from Meta to import existing ones.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <TemplateRow key={t.id} template={t} onDelete={setDeleteTarget} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-md border border-gray-200 p-1.5 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-md border border-gray-200 p-1.5 hover:bg-slate-50 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Create drawer */}
      {createOpen && (
        <CreateTemplateDrawer
          accounts={accounts}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            loadTemplates();
          }}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete template"
          message={`Delete "${deleteTarget.name}"? This calls Meta's API to delete the template and marks it as DELETED locally. Broadcasts that already used it are unaffected.`}
          confirmText="Delete" isOpen={true}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </DashboardLayout>
  );
}
